import {
  Button,
  Code,
  FormControl,
  FormLabel,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DropdownSelect } from '@/components/common/DropdownSelect'
import { useOrderScopedSideEffectJobs } from '@/hooks/useOrderConsistencyData'
import { OrderSideEffectJobsTable } from './OrderSideEffectJobsTable'
import { RefreshIconButton } from '@/components/common/RefreshIconButton'
import type { OrderListItem, OrderSideEffectJobStatus, OrderSideEffectJobType } from '@/types'

const JOB_STATUSES: OrderSideEffectJobStatus[] = [
  'pending',
  'processing',
  'retry_scheduled',
  'failed',
  'completed',
  'cancelled',
]

const JOB_TYPES: OrderSideEffectJobType[] = [
  'cache_sync',
  'payment_realtime_notify',
  'order_notification_dispatch',
  'payment_confirmation_fast_path',
  'receipt_generation',
  'contract_initialization',
  'admin_monitoring_sync',
  'merchant_webhook_event',
]

type OrderSideEffectJobsModalProps = {
  isOpen: boolean
  onClose: () => void
  order: OrderListItem | null
}

export function OrderSideEffectJobsModal({
  isOpen,
  onClose,
  order,
}: OrderSideEffectJobsModalProps) {
  const { t } = useTranslation()
  const [status, setStatus] = useState('')
  const [jobType, setJobType] = useState('')
  const [cursors, setCursors] = useState<Array<string | undefined>>([undefined])
  const pageIndex = cursors.length - 1
  const orderId = order?.orderId ?? null
  const query = useOrderScopedSideEffectJobs(
    orderId,
    {
      limit: 20,
      cursor: cursors[pageIndex],
      status: status || undefined,
      jobType: jobType || undefined,
    },
    { enabled: isOpen }
  )

  useEffect(() => {
    setStatus('')
    setJobType('')
    setCursors([undefined])
  }, [orderId])

  const resetCursor = () => setCursors([undefined])
  const data = query.data
  const statusOptions = [
    { value: '', label: t('common.all') },
    ...JOB_STATUSES.map(value => ({
      value,
      label: t(`monitoring.sideEffectJobs.statuses.${value}`),
    })),
  ]
  const jobTypeOptions = [
    { value: '', label: t('common.all') },
    ...JOB_TYPES.map(value => ({
      value,
      label: t(`monitoring.sideEffectJobs.jobTypes.${value}`),
    })),
  ]

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" isCentered>
      <ModalOverlay />
      <ModalContent maxH="90vh">
        <ModalHeader>{t('orders.sideEffectJobs.title')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody overflowY="auto">
          <VStack align="stretch" spacing={4}>
            <Text fontSize="sm" color="gray.600">
              {t('orders.sideEffectJobs.retentionNotice')}
            </Text>
            {orderId && (
              <HStack>
                <Text fontSize="sm" fontWeight="semibold">
                  {t('monitoring.sideEffectJobs.columns.order')}:
                </Text>
                <Code fontSize="sm">{orderId}</Code>
              </HStack>
            )}
            <HStack align="end" spacing={3} flexWrap="wrap">
              <FormControl maxW="220px">
                <FormLabel fontSize="xs">
                  {t('monitoring.sideEffectJobs.filters.status')}
                </FormLabel>
                <DropdownSelect
                  size="sm"
                  value={status}
                  options={statusOptions}
                  onChange={value => {
                    setStatus(value)
                    resetCursor()
                  }}
                />
              </FormControl>
              <FormControl maxW="260px">
                <FormLabel fontSize="xs">
                  {t('monitoring.sideEffectJobs.filters.jobType')}
                </FormLabel>
                <DropdownSelect
                  size="sm"
                  value={jobType}
                  options={jobTypeOptions}
                  onChange={value => {
                    setJobType(value)
                    resetCursor()
                  }}
                />
              </FormControl>
              <RefreshIconButton
                label={t('common.refresh')}
                onClick={() => query.refetch()}
                isLoading={query.isFetching}
              />
            </HStack>

            <OrderSideEffectJobsTable
              jobs={data?.jobs ?? []}
              isLoading={query.isLoading || query.isFetching}
              error={query.error}
              observedAt={data?.observedAt}
              showOrderId={false}
              emptyMessage={t('orders.sideEffectJobs.empty')}
            />

            <HStack justify="space-between">
              <Button
                size="sm"
                onClick={() => setCursors(current => current.slice(0, -1))}
                isDisabled={pageIndex === 0 || query.isFetching}
              >
                {t('common.previous')}
              </Button>
              <Text fontSize="sm" color="gray.500">
                {t('orders.sideEffectJobs.page', { page: pageIndex + 1 })}
              </Text>
              <Button
                size="sm"
                onClick={() => {
                  if (data?.pagination.nextCursor) {
                    setCursors(current => [...current, data.pagination.nextCursor ?? undefined])
                  }
                }}
                isDisabled={!data?.pagination.hasMore || query.isFetching}
              >
                {t('common.next')}
              </Button>
            </HStack>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose}>{t('common.close')}</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
